import {
  ScaffolderFieldError,
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { Box, TextField } from '@material-ui/core';

import {
  APPLICATION_DESCRIPTION_INFO,
  APPLICATION_NAME_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import { ApplicationSetBasicInfoValue } from '../types';
import { applicationSetNameFormatErrors } from '../validation';

interface ApplicationSetBasicInfoSectionProps {
  value: ApplicationSetBasicInfoValue;
  selectedProject: string;
  onChange: (value: ApplicationSetBasicInfoValue) => void;
  errors: string[];
}

const ApplicationSetBasicInfoSectionInner = ({
  value,
  selectedProject,
  onChange,
  errors,
}: ApplicationSetBasicInfoSectionProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [nameWarning, setNameWarning] = useState<string | undefined>();
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());
  const nameRef = useRef<HTMLDivElement>(null);

  const nameError =
    errors.length > 0
      ? applicationSetNameFormatErrors(value.applicationSetName)
      : '';
  const hasError = nameError !== '';

  useEffect(() => {
    if (hasError || nameWarning) {
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasError, nameWarning]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedProject) {
      setExistingNames(new Set());
    } else {
      catalogApi
        .getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'daas-application-set',
            'spec.projectId': selectedProject,
          },
          fields: ['spec.applicationSetName'],
        })
        .then(result => {
          if (cancelled) return;
          setExistingNames(
            new Set(
              result.items
                .map(e => e.spec?.applicationSetName as string)
                .filter(Boolean),
            ),
          );
        })
        .catch(() => {
          if (!cancelled) setExistingNames(new Set());
        });
    }

    return () => {
      cancelled = true;
    };
  }, [selectedProject, catalogApi]);

  useEffect(() => {
    if (!value.applicationSetName || !value.applicationSetName.trim()) {
      setNameWarning(undefined);
      return;
    }
    setNameWarning(
      existingNames.has(value.applicationSetName)
        ? `ApplicationSet "${value.applicationSetName}" already exists in project "${selectedProject}"`
        : undefined,
    );
  }, [value.applicationSetName, existingNames, selectedProject]);

  let appSetNameHelperText: React.ReactNode;
  if (nameWarning) {
    appSetNameHelperText = <ScaffolderFieldError message={nameWarning} />;
  } else if (hasError) {
    appSetNameHelperText = <ScaffolderFieldError message={nameError} />;
  } else {
    appSetNameHelperText =
      'Up to 46 characters. Allowed: lowercase letters, numbers, hyphens.';
  }

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="ApplicationSet configuration"
          description="Configure a new Deployment as a Service ApplicationSet."
          divider
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="ApplicationSet Name"
            description="Specify a unique name for the ApplicationSet."
            infoDoc={APPLICATION_NAME_INFO}
          />
          <TextField
            ref={nameRef}
            label="ApplicationSet Name"
            value={value.applicationSetName}
            onChange={e =>
              onChange({ ...value, applicationSetName: e.target.value })
            }
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            required
            error={hasError || Boolean(nameWarning)}
            inputProps={{ maxLength: 46 }}
            helperText={appSetNameHelperText}
          />
        </Box>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the ApplicationSet."
            infoDoc={APPLICATION_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e => onChange({ ...value, description: e.target.value })}
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            multiline
            inputProps={{ maxLength: 250 }}
            minRows={3}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ApplicationSetBasicInfoSection = React.memo(
  ApplicationSetBasicInfoSectionInner,
);
